import { $el, $list, getTargets } from 'src/scripts/utils/utils'
import { SidebarFiltersDesktop, SidebarFiltersMobile } from '.'
import { BaseComponent } from '../../components/base-component'
import { parseHTML, replaceNodeChildren } from '../../utils/dom'

const ELEMENTS = [
  'ShopProducts',
  'ShopProductCount',
  'ShopSortList',
  'ShopActiveFilters',
]
const PRELOADER_SELECTOR = '#ShopProductsOverlay'
const MOBILE_FILTER_SELECTOR = '#SidebarFiltersMobile'
const DESKTOP_FILTER_SELECTOR = '#SidebarFiltersDesktop'

export class ShopComponent extends BaseComponent {
  searchParamsInitial: string
  searchParamsPrev: string
  sortQuery: string = ''
  filterQuery: string = ''
  requestController?: AbortController

  constructor() {
    super()
    this.searchParamsInitial = this.getInteractiveSearchParams(
      window.location.search.slice(1),
    )
    this.searchParamsPrev = this.searchParamsInitial
    this.syncQueryState(this.searchParamsInitial)
  }

  mountComponent(): void {
    this.addListener(window, 'popstate', this.handleHistoryChange)
  }

  handleHistoryChange = (): void => {
    const newSearchParams = this.getInteractiveSearchParams(
      window.location.search.slice(1),
    )

    if (newSearchParams !== this.searchParamsPrev) {
      this.updateBySearchParams(newSearchParams, false)
    }
  }

  getInteractiveSearchParams(searchParams: string): string {
    const params = new URLSearchParams(searchParams)
    const baseParams = new URLSearchParams(this.dataset.baseParams || '')

    baseParams.forEach((value, key) => {
      if (params.get(key) === value) {
        params.delete(key)
      }
    })

    return params.toString()
  }

  syncQueryState(searchParams: string): void {
    const params = new URLSearchParams(searchParams)
    const sortValue = params.get('sort_by')

    params.delete('sort_by')
    this.sortQuery = sortValue
      ? new URLSearchParams({ sort_by: sortValue }).toString()
      : ''
    this.filterQuery = params.toString()
  }

  formatParams(searchParams: string): string {
    this.searchParamsPrev = searchParams
    this.syncQueryState(searchParams)
    return `${
      this.dataset.baseParams ? `${this.dataset.baseParams}` : ''
    }${searchParams}`
  }

  updateBySearchParams(searchParams: string, updateHistory = true): void {
    const interactiveSearchParams = this.getInteractiveSearchParams(
      searchParams ? `${searchParams}` : '',
    )
    const baseUrl = window.location.pathname
    const params = this.formatParams(interactiveSearchParams)
    const url = params ? `${baseUrl}?${params}` : baseUrl

    const { firstTarget } = getTargets(this)

    firstTarget?.focus()
    if (updateHistory) {
      this.updateURL(interactiveSearchParams, url)
    }
    this.updateContent(url)
  }

  async getFromUrl(url: string, signal: AbortSignal): Promise<Document> {
    const response = await fetch(url, { signal })

    if (!response.ok) {
      throw new Error(`Error loading shop content: ${response.status}`)
    }

    return parseHTML(await response.text())
  }

  cancelActiveRequest(): void {
    this.requestController?.abort()
  }

  async updateContent(url: string): Promise<void> {
    const preloaderElement = $el(PRELOADER_SELECTOR, this)
    const filters: SidebarFiltersDesktop[] | SidebarFiltersMobile[] = $list(
      `${MOBILE_FILTER_SELECTOR}, ${DESKTOP_FILTER_SELECTOR}`,
    )

    this.requestController?.abort()
    const requestController = new AbortController()
    this.requestController = requestController

    preloaderElement.classList.remove('hidden')
    filters.forEach(filter => {
      filter.preloader.classList.remove('hidden')
    })

    try {
      const html = await this.getFromUrl(url, requestController.signal)

      if (this.requestController !== requestController) {
        return
      }

      ELEMENTS.forEach(elementId => {
        const element = $el(`#${elementId}`)
        const newElement = $el(`#${elementId}`, html)

        replaceNodeChildren(element, newElement)
      })
      filters.forEach(filter => {
        filter.updateHTML(html)
      })
      this.emit('filtersUpdated', {})
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.log('Error in shop component')
      }
    } finally {
      if (this.requestController !== requestController) {
        return
      }

      preloaderElement.classList.add('hidden')
      filters.forEach(filter => {
        filter.preloader.classList.add('hidden')
      })
      this.requestController = undefined
    }
  }

  updateURL(searchParams, url: string): void {
    history.pushState({ searchParams }, '', url)
  }

  reset(): void {
    this.sortQuery = ''
    this.filterQuery = ''
  }
}
